import { useState, useRef, useContext, useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import { FaPaperclip } from "react-icons/fa";
import SupportService from "../../../services/SupportService";
import AlertService from "../../../services/AlertService";
import AuthContext from "../../../auth/AuthContext";
import CloseIcon from "../../../assets/icons/Close.svg";

const Comment = ({ ticketId, submitHandler, cancelHandler }) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [comment, setComment] = useState("");
  const authContext = useContext(AuthContext);
  const fileInputRef = useRef();
  const submitControllerRef = useRef(null);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files).map((file) => ({
      fileName: file.name,
      filePath: file.name,
      data: file,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    fileInputRef.current.value = "";
  };

  const removeFile = (fileName) => {
    setFiles(files.filter((f) => f.fileName !== fileName));
  };

  const clearForm = () => {
    setComment("");
    setFiles([]);
  };


const handleSubmit = async (e) => {
  e.preventDefault();
  if (!comment.trim()) return;

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  const formData = new FormData();
  files.forEach((f) => formData.append("files", f.data));
  formData.append("ticketId", ticketId);
  formData.append("comment", comment);

  try {
    const res = await SupportService.SaveComment(
      formData,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (res.isSuccess) {
      clearForm();
      submitHandler();
    } else {
      AlertService.error(res.errorMessage);
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    AlertService.error("An error occurred while submitting comment");
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group controlId="commentTextarea" className="mb-3">
        <Form.Label className="support-label">Add Comment</Form.Label>
        <Form.Control
          as="textarea"
          rows={10}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Write your comment here..."
          disabled={loading}
          className="mt-1 custom-input-color"
        />
      </Form.Group>

      <div className="mb-3 d-flex align-items-start justify-content-between border p-1 custom-input-color rounded">
        {files.length > 0 ? (
          <div className="d-flex flex-wrap gap-2">
            {files.map((file, i) => (
              <div
                key={i}
                className="d-flex align-items-center px-1 py-1 pdf-color border m-2"
                style={{ minWidth: 180 }}
              >
                <span className="text-truncate">
                  {file.fileName}
                </span>
                <Button
                  variant="link"
                  className="text-danger ms-2 p-0"
                  style={{
                    fontSize: "1.25rem",
                    lineHeight: 0,
                    textDecoration: "none",
                    marginTop: '-30px',
                    marginRight: '-10px'
                  }}
                  title="Remove File"
                  onClick={() => removeFile(file.fileName)}
                  aria-label={`Remove ${file.fileName}`}
                >
                  <img src={CloseIcon} alt="Close" height='12px'></img>
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div />
        )}
        <div className="ms-auto">
          <button
            onClick={() => fileInputRef.current.click()}
            disabled={loading}
            className="d-flex align-items-center border-0 btn "
          >
            <FaPaperclip className="me-2 support-label" /> <span className="support-label">Attach File</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="d-none"
            onChange={handleFileChange}
            multiple
            accept="application/pdf"
          />
        </div>
      </div>
      <div className="text-end">
        <button
          className="me-3 px-5 py-2 btn-outline btn"
          type="button"
          onClick={() => {
            clearForm();
            cancelHandler();
          }}
          disabled={loading}
        >
          Cancel
        </button>
        <Button type="submit" variant="primary" className="px-5 py-2" disabled={loading}>
          {loading ? "Adding..." : "Submit"}
        </Button>
      </div>
    </Form>
  );
};

export default Comment;
